import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
} from "@mindstonehq/ui";
import { Link } from "react-router";
import type { ResolvedPerson } from "./enrich-community-team";
import { Trans, useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";
import { i18n } from "@lingui/core";

interface EventCommunityTeamProps {
  atEvent: ResolvedPerson[];
  city: ResolvedPerson[];
  locationName: string;
}

function getInitials(displayName: string, username: string): string {
  const source = displayName.trim() || username.trim();
  return source
    .split(/\s+/)
    .map((part) => part[0] ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function roleLabel(role: ResolvedPerson["role"]): string {
  return role === "organiser" ? i18n._(msg`Organiser`) : i18n._(msg`Associate`);
}

function rolePillClass(role: ResolvedPerson["role"]): string {
  if (role === "organiser") {
    // eslint-disable-next-line lingui/no-unlocalized-strings -- Tailwind class names
    return "border-transparent bg-tertiary/20 text-tertiary";
  }
  // eslint-disable-next-line lingui/no-unlocalized-strings -- Tailwind class names
  return "border-transparent bg-muted text-muted-foreground";
}

function TeamRow({ person }: { person: ResolvedPerson }) {
  const { t } = useLingui();
  const profileUrl = `/profile/${encodeURIComponent(person.profile.username)}`;
  return (
    <Link
      to={profileUrl}
      aria-label={t(msg`View ${person.profile.displayName}'s Mindstone profile`)}
      className="group flex items-start gap-3 rounded-md border border-border/70 bg-background/40 p-2.5 transition-colors hover:bg-muted/40"
    >
      <Avatar className="h-9 w-9 shrink-0">
        {person.profile.profilePictureURL ? (
          <AvatarImage
            src={person.profile.profilePictureURL}
            alt={person.profile.displayName}
          />
        ) : null}
        <AvatarFallback className="text-xs font-medium">
          {getInitials(person.profile.displayName, person.profile.username)}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-medium text-foreground">
            {person.profile.displayName}
          </p>
          <Badge
            variant="secondary"
            className={cn("h-5 shrink-0 px-1.5 text-[10px] font-medium", rolePillClass(person.role))}
          >
            {roleLabel(person.role)}
          </Badge>
        </div>
        {person.profile.tagLine ? (
          <p className="truncate text-xs text-muted-foreground">
            {person.profile.tagLine}
          </p>
        ) : null}
      </div>
    </Link>
  );
}

function TeamTier({
  label,
  mutedLabel,
  people,
}: {
  label: string;
  mutedLabel?: boolean;
  people: ResolvedPerson[];
}) {
  if (people.length === 0) return null;
  return (
    <section className="space-y-2">
      <p
        className={cn(
          "text-[11px] font-semibold uppercase tracking-wide",
          mutedLabel ? "text-muted-foreground" : "text-foreground",
        )}
      >
        {label}
      </p>
      <div className="space-y-2">
        {people.map((person, index) => (
          <TeamRow
            key={`${person.role}-${person.profile.username}-${index}`}
            person={person}
          />
        ))}
      </div>
    </section>
  );
}

export default function EventCommunityTeam({
  atEvent,
  city,
  locationName,
}: EventCommunityTeamProps) {
  const { t } = useLingui();
  if (atEvent.length === 0 && city.length === 0) return null;

  const trimmedLocation = locationName.trim();
  const subtitle = trimmedLocation
    ? t(msg`The organisers and associates helping bring Mindstone events to ${trimmedLocation}.`)
    : t(msg`The organisers and associates helping bring Mindstone events.`);

  return (
    <Card>
      <CardHeader className="space-y-1.5">
        <CardTitle><Trans>Meet your local team</Trans></CardTitle>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <TeamTier label={t(msg`At this event`)} people={atEvent} />
        <TeamTier
          label={trimmedLocation ? t(msg`In ${trimmedLocation}`) : t(msg`In your city`)}
          mutedLabel
          people={city}
        />
      </CardContent>
    </Card>
  );
}
